// Paso 1 — los archivos.
//
// Empresa y grupo van ANTES, y no es una preferencia de orden: son parámetros
// de ENTRADA del flujo que lee los archivos. Si se pidieran después, cambiarlos
// obligaría a releer, y el usuario no tendría por qué saberlo.
//
// PERO LA ZONA DE ARRASTRE NO SE BLOQUEA POR ESO. Estaba apagada hasta elegir
// los dos, y soltar un archivo encima no hacía NADA: ni lo cogía ni decía por
// qué. El gesto natural es traer el archivo primero. Ahora se queda, y lo que
// falta se marca en los campos que faltan y en el botón de leer, que es donde
// de verdad hace falta tenerlo — leer sin empresa ni grupo sí es imposible.
//
// Se admiten VARIOS archivos y sale UN pedido: el cliente puede mandarlo
// repartido —el Excel de la mercancía y el PDF de la nota, dos hojas que
// llegaron por separado— y el flujo los junta. La validación es de todo o nada,
// igual que en el backend: colar los buenos y callarse el malo dejaría un
// pedido incompleto que el usuario cree completo.
import { useTranslation } from 'react-i18next'
import { Loader } from '@/components/common/Loader'
import { ZonaArrastre } from '@/components/common/ZonaArrastre'
import type { GrupoCliente } from '@/types'
import { campoCls, cromado, etiquetaCls, pastillaCls } from './cromado'

const EXTENSIONES = ['.xlsx', '.xls', '.pdf']
const MAX_MB = 10
const MAX_ARCHIVOS = 5
const MAX_TOTAL_MB = 20

interface Props {
  dark: boolean
  empresas: string[]
  empresa: string
  grupo: GrupoCliente | ''
  archivos: File[]
  leyendo: boolean
  error: { codigo: string; valores?: Record<string, string | number> } | null
  onEmpresa: (v: string) => void
  onGrupo: (v: GrupoCliente) => void
  onArchivos: (f: File[]) => void
  onError: (codigo: string, valores?: Record<string, string | number>) => void
  onLeer: () => void
}

function kb(bytes: number): string {
  return `${(bytes / 1024).toLocaleString(undefined, { maximumFractionDigits: 0 })} KB`
}

export function PasoArchivo({
  dark, empresas, empresa, grupo, archivos, leyendo, error,
  onEmpresa, onGrupo, onArchivos, onError, onLeer,
}: Props) {
  const { t } = useTranslation()
  const c = cromado(dark)
  // Con una sola empresa, elegir no es una decisión: se preselecciona y se
  // bloquea para que no parezca que falta algo por rellenar.
  const unaSola = empresas.length === 1
  const total = archivos.reduce((a, f) => a + f.size, 0)
  const pasaDelTotal = total > MAX_TOTAL_MB * 1024 * 1024
  const faltaContexto = !empresa || !grupo
  // Marcar en gris, no en rojo, y sólo cuando ya hay archivo: hasta entonces no
  // es que falte nada, es que el usuario aún no ha llegado.
  const marcar = faltaContexto && archivos.length > 0
  const avisoCampo = marcar
    ? (dark ? 'ring-1 ring-white/20 rounded-lg' : 'ring-1 ring-slate-300 rounded-lg')
    : ''

  if (leyendo) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20">
        <Loader label={t('salesOrders.desdeArchivo.leyendo', { count: archivos.length })} />
        <p className={`text-[11px] max-w-sm text-center leading-relaxed ${c.mutedTxt}`}>
          {t('salesOrders.desdeArchivo.leyendoPista')}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className={`${etiquetaCls} ${c.mutedTxt}`}>
            {t('salesOrders.desdeArchivo.empresa')}
            {marcar && !empresa && (
              <span className={`ml-1.5 font-normal normal-case ${c.dimTxt}`}>
                {t('salesOrders.desdeArchivo.pendiente')}
              </span>
            )}
          </label>
          <select
            value={empresa}
            disabled={unaSola}
            onChange={(e) => onEmpresa(e.target.value)}
            className={`${campoCls(dark)} ${marcar && !empresa ? avisoCampo : ''} ${
              unaSola ? 'opacity-70 cursor-default' : 'cursor-pointer'
            }`}
          >
            {!unaSola && <option value="">{t('salesOrders.desdeArchivo.elige')}</option>}
            {empresas.map((e) => (
              <option key={e} value={e}>{e.toUpperCase()}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <label className={`${etiquetaCls} ${c.mutedTxt}`}>
            {t('salesOrders.desdeArchivo.grupo')}
            {marcar && !grupo && (
              <span className={`ml-1.5 font-normal normal-case ${c.dimTxt}`}>
                {t('salesOrders.desdeArchivo.pendiente')}
              </span>
            )}
          </label>
          <div className={`flex gap-2 ${marcar && !grupo ? `p-1 -m-1 ${avisoCampo}` : ''}`} role="radiogroup">
            {(['NAC', 'EXP'] as const).map((g) => (
              <button
                key={g}
                type="button"
                role="radio"
                aria-checked={grupo === g}
                onClick={() => onGrupo(g)}
                className={pastillaCls(dark, grupo === g)}
              >
                {t(`salesOrders.desdeArchivo.grupos.${g}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {archivos.length > 0 && (
        <div className={`rounded-xl border overflow-hidden ${c.panelBorde}`}>
          {archivos.map((archivo, i) => (
            <div
              key={`${archivo.name}-${archivo.size}-${i}`}
              className={`flex items-center gap-3 px-4 py-2.5 ${c.panel} ${
                i > 0 ? `border-t ${c.panelBorde}` : ''
              }`}
            >
              <span className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${
                dark ? 'bg-white/[0.06] text-slate-400' : 'bg-white text-slate-400'
              }`}>
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <path d="M14 2v6h6" />
                </svg>
              </span>
              <div className="flex-1 min-w-0">
                <p className={`text-[12px] font-medium truncate ${c.mainTxt}`}>{archivo.name}</p>
                <p className={`text-[10px] tabular-nums ${c.dimTxt}`}>{kb(archivo.size)}</p>
              </div>
              <button
                type="button"
                onClick={() => onArchivos(archivos.filter((_, j) => j !== i))}
                aria-label={t('salesOrders.desdeArchivo.quitar')}
                className={`px-2 py-1 rounded-lg text-[11px] cursor-pointer transition-colors ${
                  dark ? 'text-slate-500 hover:text-slate-300 hover:bg-white/6'
                       : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                }`}
              >
                {t('salesOrders.desdeArchivo.quitar')}
              </button>
            </div>
          ))}

          {archivos.length > 1 && (
            <div className={`flex items-center gap-2 px-4 py-2 border-t ${c.panelBorde} ${
              dark ? 'bg-white/[0.02]' : 'bg-white'
            }`}>
              <span className={`text-[11px] ${c.mutedTxt}`}>
                {t('salesOrders.desdeArchivo.totalArchivos', {
                  count: archivos.length, peso: kb(total),
                })}
              </span>
              <span className={`text-[10px] ml-auto ${pasaDelTotal ? 'text-red-500 font-semibold' : c.dimTxt}`}>
                {t('salesOrders.desdeArchivo.topeTotal', { mb: MAX_TOTAL_MB })}
              </span>
            </div>
          )}
        </div>
      )}

      {archivos.length < MAX_ARCHIVOS && (
        <ZonaArrastre
          dark={dark}
          extensiones={EXTENSIONES}
          maxBytes={MAX_MB * 1024 * 1024}
          huecos={MAX_ARCHIVOS - archivos.length}
          onArchivos={(nuevos) => onArchivos([...archivos, ...nuevos])}
          onError={onError}
        />
      )}

      {faltaContexto ? (
        <p className={`text-[11px] ${c.dimTxt}`}>
          {t('salesOrders.desdeArchivo.faltaContexto')}
        </p>
      ) : (
        <p className={`text-[11px] ${c.dimTxt}`}>
          {t('salesOrders.desdeArchivo.variosPista', { max: MAX_ARCHIVOS })}
        </p>
      )}

      {(error || pasaDelTotal) && (
        <div className={`px-4 py-3 rounded-xl border text-[12px] ${
          dark ? 'bg-red-500/[0.07] border-red-500/20 text-red-300'
               : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          {pasaDelTotal
            ? t('salesOrders.desdeArchivo.errores.archivos_demasiado_grandes', { maxMb: MAX_TOTAL_MB })
            : t(`salesOrders.desdeArchivo.errores.${error!.codigo}`, {
                ...error!.valores,
                defaultValue: t('salesOrders.desdeArchivo.errores.generico'),
              })}
        </div>
      )}

      {archivos.length > 0 && (
        <button
          type="button"
          disabled={pasaDelTotal || faltaContexto}
          onClick={onLeer}
          title={faltaContexto ? t('salesOrders.desdeArchivo.faltaContexto') : undefined}
          className={`w-full px-4 py-2.5 rounded-lg text-[12px] font-semibold transition-colors shadow-sm ${
            pasaDelTotal || faltaContexto
              ? `cursor-not-allowed ${dark ? 'bg-white/8 text-slate-500' : 'bg-slate-200 text-slate-400'}`
              : 'bg-sky-600 text-white hover:bg-sky-500 cursor-pointer'
          }`}
        >
          {faltaContexto
            ? t('salesOrders.desdeArchivo.leerFalta', {
                campos: [
                  !empresa ? t('salesOrders.desdeArchivo.empresa') : null,
                  !grupo ? t('salesOrders.desdeArchivo.grupo') : null,
                ].filter(Boolean).join(' · '),
              })
            : t('salesOrders.desdeArchivo.leer', { count: archivos.length })}
        </button>
      )}
    </div>
  )
}
